import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Banknote,
  CheckCircle,
  XCircle,
  User,
  Clock,
  DollarSign,
  FileText,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Loader2
} from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PendingExternalPayments = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [processingId, setProcessingId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  useEffect(() => {
    fetchPendingPayments();
  }, []);

  const fetchPendingPayments = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/admin/pending-payments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPayments(res.data.pending_payments || []);
    } catch (error) {
      console.error("Error fetching pending payments:", error);
      toast.error("Error al cargar pagos pendientes");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (clientId, clientName) => {
    if (!confirm(`¿Confirmas aprobar el pago de ${clientName}? Se activará su cuenta.`)) {
      return;
    }

    setProcessingId(clientId);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API}/admin/pending-payments/${clientId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message);
      setPayments(payments.filter(p => p.id !== clientId));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al aprobar pago");
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (clientId, clientName) => {
    if (!rejectReason.trim()) {
      toast.error("Por favor indica una razón para el rechazo");
      return;
    }

    if (!confirm(`¿Confirmas rechazar el pago de ${clientName}?`)) {
      return;
    }

    setProcessingId(clientId);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API}/admin/pending-payments/${clientId}/reject?reason=${encodeURIComponent(rejectReason)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message);
      setPayments(payments.filter(p => p.id !== clientId));
      setRejectReason("");
      setExpandedId(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al rechazar pago");
    } finally {
      setProcessingId(null);
    }
  };

  const getPlanColor = (plan) => {
    switch(plan) {
      case "vip": return "bg-amber-100 text-amber-800 border-amber-200";
      case "premium": return "bg-blue-100 text-blue-800 border-blue-200";
      default: return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-navy-500" />
      </div>
    );
  }

  if (payments.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-navy-100 p-6 text-center">
        <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <CheckCircle className="w-6 h-6 text-emerald-600" />
        </div>
        <p className="font-medium text-navy-900">Sin pagos pendientes</p>
        <p className="text-sm text-navy-500 mt-1">Todos los pagos externos han sido procesados</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-navy-100 overflow-hidden" data-testid="pending-payments-panel">
      {/* Header */}
      <div className="bg-amber-50 border-b border-amber-100 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Banknote className="w-5 h-5 text-amber-600" />
          <h3 className="font-semibold text-amber-900">Pagos Externos Pendientes</h3>
        </div>
        <span className="px-2 py-1 bg-amber-200 text-amber-800 text-sm font-medium rounded-full">
          {payments.length}
        </span>
      </div>

      {/* List */}
      <div className="divide-y divide-navy-100">
        <AnimatePresence>
          {payments.map((payment) => (
            <motion.div
              key={payment.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, height: 0 }}
              className="p-4"
            >
              {/* Main Row */}
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 flex-1">
                  <div className="w-10 h-10 bg-navy-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5 text-navy-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold text-navy-900">{payment.name}</h4>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded border ${getPlanColor(payment.plan_type)}`}>
                        {payment.plan_name || payment.plan_type?.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-sm text-navy-500">{payment.email}</p>
                    <div className="flex items-center gap-4 mt-1 text-xs text-navy-500">
                      <span className="flex items-center gap-1">
                        <DollarSign className="w-3 h-3" />
                        ${payment.plan_price} USD
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(payment.created_at).toLocaleDateString('es-MX')}
                      </span>
                      <span>Vendedor: {payment.vendedor_name}</span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    size="sm"
                    onClick={() => handleApprove(payment.id, payment.name)}
                    disabled={processingId === payment.id}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid={`approve-${payment.id}`}
                  >
                    {processingId === payment.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <CheckCircle className="w-4 h-4 mr-1" />
                    )}
                    Aprobar
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setExpandedId(expandedId === payment.id ? null : payment.id)}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                  >
                    {expandedId === payment.id ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>

              {/* Payment Notes */}
              {payment.payment_notes && (
                <div className="mt-3 p-3 bg-amber-50 rounded-lg">
                  <div className="flex items-start gap-2">
                    <FileText className="w-4 h-4 text-amber-600 mt-0.5" />
                    <div>
                      <p className="text-xs font-medium text-amber-800">Notas del vendedor:</p>
                      <p className="text-sm text-amber-900 mt-1">{payment.payment_notes}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Expanded Reject Section */}
              <AnimatePresence>
                {expandedId === payment.id && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-4 p-4 bg-red-50 rounded-lg border border-red-200"
                  >
                    <div className="flex items-start gap-2 mb-3">
                      <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5" />
                      <p className="text-sm text-red-800">
                        Al rechazar, el cliente no será activado y el vendedor será notificado.
                      </p>
                    </div>
                    <textarea
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      placeholder="Razón del rechazo (ej: comprobante no válido, monto incorrecto...)"
                      className="w-full px-3 py-2 border border-red-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500 min-h-[60px]"
                    />
                    <div className="flex justify-end gap-2 mt-3">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setExpandedId(null);
                          setRejectReason("");
                        }}
                      >
                        Cancelar
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleReject(payment.id, payment.name)}
                        disabled={processingId === payment.id || !rejectReason.trim()}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        {processingId === payment.id ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-1" />
                        ) : (
                          <XCircle className="w-4 h-4 mr-1" />
                        )}
                        Rechazar Pago
                      </Button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default PendingExternalPayments;
